import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FullView from '../../components/5templates/FullView';
import DashboardTab from './DashboardTab';
import DashboardDataEntryTab from './DashboardDataEntryTab';
import LoaderBackdrop from '../../components/2compounds/LoaderBackdrop';
import DeleteConfirmationModal from '../../components/1elements/Modals/DeleteConfirmationModal';
import NoData from '../../components/2compounds/NoData';
import ApiService from '../../common/ApiCalls';

import PeopleTab from '../../v2/pages/FullDetailsPage/PeopleTab';
import LocationTab from '../../v2/pages/FullDetailsPage/LocationTab';
import ServiceTab from '../../v2/pages/FullDetailsPage/ServiceTab';
import AccountTab from '../../v2/pages/FullDetailsPage/AccountTab';
import { mergeValuesIntoCards } from '../../v2/shared/utils/merge-values-to-cards';
import { loadAssetServices } from './loadAssetServices';
import { resolveAssetCoordinates } from './resolveAssetCoordinates';
import HistoryDateControl, {
  browserTimezone,
  calendarToday
} from './HistoryDateControl';
import HistoricalLayout from './HistoricalLayout';

import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
  loader: {
    position: 'absolute',
    zIndex: 10
  },
  mainDiv: {},
  accordion: {
    marginBottom: '8px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    '&:before': { display: 'none' }
  },
  imageSliderPosition: {
    maxWidth: '100%',
    maxHeight: '100%',
    overflow: 'hidden'
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      display: 'flex',
      flexDirection: 'column'
    }
  },
  fieldContainer: {
    marginBottom: 12
  },
  listContainer: {
    position: 'relative',

    '&>h2': {
      margin: 0,
      marginBottom: '24px'
    }
  },
  listItem: {
    padding: '10px 0',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#eee'
    },
    '& span': {
      fontSize: '18px'
    },
    '& span.title': {
      fontWeight: 700
    },
    '& span.subtitle': {
      fontWeight: 400,
      color: '#00000099',
      lineHeight: '30px'
    }
  }
}));

export default function ConsumerTabs() {
  const { tabName, id } = useParams();
  const today = calendarToday();
  const [selection, setSelection] = useState(() => ({ id, date: today }));
  const historyRevision = 0;
  const date = selection.id === id ? selection.date : today;
  const timezone = browserTimezone();
  const objectType = {
    tab0: 'asset',
    tab1: 'party',
    tab2: 'service',
    tab3: 'contract'
  }[tabName];
  // This state belongs to the open Tab View, never to dashboard filters or widgets.
  useEffect(() => {
    setSelection({ id, date: calendarToday() });
  }, [id]);
  const controls = objectType ? (
    <>
      <HistoryDateControl
        date={date}
        today={today}
        timezone={timezone}
        onChange={(value) => setSelection({ id, date: value })}
      />

    </>
  ) : null;
  if (objectType && date !== today)
    return (
      <FullView>
        {controls}
        <HistoricalLayout
          key={`${id}:${date}:${objectType}:${historyRevision}`}
          id={id}
          date={date}
          timezone={timezone}
          objectType={objectType}
        />
      </FullView>
    );
  return (
    <CurrentTabs
      key={`${id}:${tabName}:${historyRevision}`}
      historyControls={controls}
    />
  );
}

// Historical screens never mount the current-value loaders or editing forms.
function CurrentTabs({ historyControls }) {
  const classes = useStyles();
  const { tabName, id } = useParams();

  const [loading, setLoading] = useState(false);
  const [hasNoData, setHasNoData] = useState(false);

  const [assetInfo, setAssetInfo] = useState(null);

  const [assetLayout, setAssetLayout] = useState(null);
  const [assetCards, setAssetCards] = useState([]);

  const [partyList, setPartyList] = useState([]);
  const [partyLayout, setPartyLayout] = useState(null);

  const [serviceList, setServiceList] = useState([]);
  const [serviceLayout, setServiceLayout] = useState(null);

  const [contractList, setContractList] = useState([]);
  const [contractLayout, setContractLayout] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  async function loadAsset() {
    try {
      const asset = await ApiService().getAsset(id);
      setAssetInfo(asset);
    } catch (err) {
      console.error('Error loading asset info:', err);
    }
  }

  async function loadAssetData() {
    setLoading(true);
    try {
      const layoutResp = await ApiService().getLayoutByAsset('asset', id);
      if (!layoutResp || layoutResp.length === 0) {
        setHasNoData(true);
        return;
      }
      const fetchedLayout = layoutResp[0];
      setAssetLayout(fetchedLayout);

      const fids = [];
      fetchedLayout.cards.forEach((card) => {
        card.items.forEach((i) => {
          if (i.item_type === 'field') {
            fids.push(i.field_id);
          }
        });
      });

      if (fids.length === 0) {
        setAssetCards(fetchedLayout.cards);
        return;
      }

      const valResp = await ApiService().getValues(id, 'asset');
      // Filtering response due to this method returns all fields across all entities
      const locationFieldData = valResp.filter(
        (item) => item.object_type === 'asset'
      );
      const valMap = {};
      (locationFieldData || []).forEach((v) => {
        valMap[v.field_id] = v.value;
      });

      const merged = mergeValuesIntoCards(fetchedLayout.cards, valMap);
      setAssetCards(merged);
    } catch (e) {
      console.error('Error loading asset layout:', e);
      setHasNoData(true);
    } finally {
      setLoading(false);
    }
  }

  async function loadPartyData() {
    setLoading(true);
    try {
      const assetRes = await ApiService().getAsset(id);
      setAssetInfo(assetRes);

      let newPartyList = [];
      if (!assetRes.party_ids || assetRes.party_ids.length === 0) {
        newPartyList = [];
      } else {
        const arr = [];
        for (const pId of assetRes.party_ids) {
          const p = await ApiService().getPartyById(pId);
          arr.push(p);
        }
        newPartyList = arr;
      }
      setPartyList(newPartyList);

      const layoutResp = await ApiService().getLayoutByAsset('party', id);
      if (!layoutResp || layoutResp.length === 0) {
        setPartyLayout(null);
      } else {
        setPartyLayout(layoutResp[0]);
      }
    } catch (err) {
      console.error('Error loading party data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadServiceData() {
    setLoading(true);
    try {
      const apiService = ApiService();
      const assetRes = await apiService.getAsset(id);
      setAssetInfo(assetRes);

      const newServiceList = await loadAssetServices(apiService, assetRes);
      setServiceList(newServiceList);

      const layoutResp = await apiService.getLayoutByAsset('service', id);
      if (!layoutResp || layoutResp.length === 0) {
        setServiceLayout(null);
      } else {
        setServiceLayout(layoutResp[0]);
      }
    } catch (err) {
      console.error('Error loading services data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadContractData() {
    setLoading(true);
    try {
      const assetRes = await ApiService().getAsset(id);
      setAssetInfo(assetRes);

      const contracts = await ApiService().getContractsByAssetId(id);
      if (contracts && contracts.length) {
        setContractList(contracts);
      } else {
        setContractList([]);
      }

      const layoutResp = await ApiService().getLayoutByAsset('contract', id);
      if (!layoutResp || layoutResp.length === 0) {
        setContractLayout(null);
      } else {
        setContractLayout(layoutResp[0]);
      }
    } catch (err) {
      console.error('Error loading contracts data:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAsset();

    if (tabName === 'tab0') {
      loadAssetData();
    } else if (tabName === 'tab1') {
      loadPartyData();
    } else if (tabName === 'tab2') {
      loadServiceData();
    } else if (tabName === 'tab3') {
      loadContractData();
    }
    // eslint-disable-next-line
  }, [tabName, id]);

  if (tabName === 'tab4') {
    return (
      <FullView>
        {historyControls}
        <LoaderBackdrop open={loading} className={classes.loader} />
        <DashboardTab />
      </FullView>
    );
  }

  if (tabName === 'tab5') {
    return (
      <FullView>
        {historyControls}
        <LoaderBackdrop open={loading} className={classes.loader} />
        <DashboardDataEntryTab />
      </FullView>
    );
  }

  if (tabName === 'tab0') {
    if (hasNoData) {
      return (
        <FullView>
          {historyControls}
          <LoaderBackdrop open={loading} className={classes.loader} />
          <NoData />
        </FullView>
      );
    }

    if (!assetLayout) {
      return (
        <FullView>
          {historyControls}
          <LoaderBackdrop open={loading} className={classes.loader} />
          <NoData />
        </FullView>
      );
    }

    return (
      <FullView>
        {historyControls}
        <LoaderBackdrop open={loading} className={classes.loader} />

        <div className={classes.listContainer}>
          <h2>Location</h2>

          <LocationTab
            addressCoords={resolveAssetCoordinates(assetInfo)}
            fieldsData={assetCards}
            showLoader={setLoading}
          />
        </div>

        <DeleteConfirmationModal
          open={isDeleteModalOpen}
          setOpen={setIsDeleteModalOpen}
          onSubmit={() => {}}
        />
      </FullView>
    );
  }

  if (tabName === 'tab1') {
    return (
      <FullView>
        {historyControls}
        <LoaderBackdrop open={loading} className={classes.loader} />

        <div className={classes.listContainer}>
          <h2>People for {assetInfo?.name}</h2>
          {partyList.length === 0 ? (
            <p>No People Found.</p>
          ) : (
            <PeopleTab
              peopleData={partyList}
              layout={partyLayout}
              showLoader={setLoading}
            />
          )}
        </div>

        <DeleteConfirmationModal
          open={isDeleteModalOpen}
          setOpen={setIsDeleteModalOpen}
          onSubmit={() => {}}
        />
      </FullView>
    );
  }

  if (tabName === 'tab2') {
    return (
      <FullView>
        {historyControls}
        <LoaderBackdrop open={loading} className={classes.loader} />

        <div className={classes.listContainer}>
          <h2>Service List for {assetInfo?.name}</h2>
          {serviceList.length === 0 ? (
            <p>No Services Found.</p>
          ) : (
            <ServiceTab
              serviceList={serviceList}
              layout={serviceLayout}
              showLoader={setLoading}
            />
          )}
        </div>

        <DeleteConfirmationModal
          open={isDeleteModalOpen}
          setOpen={setIsDeleteModalOpen}
          onSubmit={() => {}}
        />
      </FullView>
    );
  }

  if (tabName === 'tab3') {
    return (
      <FullView>
        {historyControls}
        <LoaderBackdrop open={loading} className={classes.loader} />

        <div className={classes.listContainer}>
          <h2>Accounts for {assetInfo?.name}</h2>
          {contractList.length === 0 ? (
            <p>No Contracts Found.</p>
          ) : (
            <AccountTab
              accountsList={contractList}
              layout={contractLayout}
              showLoader={setLoading}
            />
          )}
        </div>

        <DeleteConfirmationModal
          open={isDeleteModalOpen}
          setOpen={setIsDeleteModalOpen}
          onSubmit={() => {}}
        />
      </FullView>
    );
  }

  return (
    <FullView>
      {historyControls}
      <LoaderBackdrop open={loading} className={classes.loader} />
      <NoData />
    </FullView>
  );
}
